import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { assignJobCrew, assignJobEmployee, fetchCrewsAndEmployees } from '../../api/jobWorkflow'
import type { EmployeeOption } from '../../api/jobWorkflow'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

interface JobAssignmentFieldsProps {
  jobId: string
  crewId?: string | null
  employeeId?: string | null
  onAssigned?: () => void
  compact?: boolean
}

export function JobAssignmentFields({
  jobId,
  crewId,
  employeeId,
  onAssigned,
  compact = false,
}: JobAssignmentFieldsProps) {
  const [crews, setCrews] = useState<{ id: string; name: string }[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    fetchCrewsAndEmployees().then(({ crews: c, employees: e, loadError: err }) => {
      setCrews(c)
      setEmployees(e)
      if (err) setLoadError(err)
      else if (c.length === 0 && e.length === 0) {
        setLoadError('empty')
      } else setLoadError(null)
    })
  }, [])

  const handleCrew = async (id: string) => {
    setSaving(true)
    try {
      await assignJobCrew(jobId, id || null)
      onAssigned?.()
    } finally {
      setSaving(false)
    }
  }

  const handleEmployee = async (id: string) => {
    setSaving(true)
    try {
      await assignJobEmployee(jobId, id || null)
      onAssigned?.()
    } finally {
      setSaving(false)
    }
  }

  const labelCls = compact
    ? 'block text-xs font-medium text-gray-600 mb-1'
    : 'block text-sm font-medium text-gray-700 mb-2'
  const inputCls =
    'w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'

  return (
    <div className="space-y-2">
    {loadError && (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        {loadError === 'empty' ? (
          <>
            No crews or employees found.{' '}
            <Link to="/settings/team" className="underline font-medium">
              Add them in Team
            </Link>
            .
          </>
        ) : (
          loadError
        )}
      </p>
    )}
    <div className={compact ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
      <div>
        <label className={labelCls}>Crew</label>
        <select
          value={crewId || ''}
          disabled={saving}
          onChange={(e) => handleCrew(e.target.value)}
          className={inputCls}
        >
          <option value="">Unassigned</option>
          {crews.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Employee</label>
        <select
          value={employeeId || ''}
          disabled={saving}
          onChange={(e) => handleEmployee(e.target.value)}
          className={inputCls}
        >
          <option value="">Unassigned</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {formatEmployeeLabel(e)}
            </option>
          ))}
        </select>
      </div>
    </div>
    </div>
  )
}
